<script lang="ts" setup>
import { computed, ref } from 'vue'
import type { ScrollChapter } from '../composables/useInfiniteChapter'
import ScrollChapterUnlock from './ScrollChapterUnlock.flipnovel.vue'
import { CHAPTER_LOCK } from '@/constants'
import { brand } from '@/config/brands'

interface Props {
  chapter: ScrollChapter
  themeFont: {
    chapterTitle: string
    chapterContent: string
    paragraphSpacing: string
  }
  memberCoin: number
  isLastChapter?: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'unlock', chapterId: string, autoLock: boolean): void
}>()

const unlockRef = ref<InstanceType<typeof ScrollChapterUnlock>>()

const paragraphs = computed(() => {
  if (!props.chapter.content)
    return []
  return props.chapter.content.split(/[\n]/).filter(Boolean)
})

const isLocked = computed(() => props.chapter.lockStatus === CHAPTER_LOCK.LOCK)
const unlockDividerBookIcon = computed(() => `url(/images/${brand}/chapter/unlock-divider-book.svg)`)

function handleUnlock() {
  const autoLock = unlockRef.value?.autoLock ?? true
  emit('unlock', props.chapter.id, autoLock)
}
</script>

<template>
  <div class="chapter-item px-20px py-10">
    <h2 :style="themeFont.chapterTitle" class="chapter-title font-800">
      {{ chapter.name }}
    </h2>

    <div :style="themeFont.chapterContent" class="chapter-content">
      <p v-for="(para, idx) in paragraphs" :key="idx" :style="themeFont.paragraphSpacing">
        {{ para }}
      </p>
    </div>

    <ScrollChapterUnlock
      v-if="isLocked"
      ref="unlockRef"
      :chapter="chapter"
      :member-coin="memberCoin"
      @unlock="handleUnlock"
    />

    <div v-if="!isLastChapter && !isLocked" class="chapter-divider">
      <span class="divider-line divider-line-left" />
      <span class="divider-icon" aria-hidden="true" />
      <span class="divider-line divider-line-right" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.chapter-item {
  min-height: 50vh;
}

.chapter-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 30px 0;
  gap: 12px;
  position: relative;
  top: 36px;
}

.divider-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  display: inline-block;
  background-color: var(--unlock-divider-color, #A8AFBB);
  -webkit-mask-image: v-bind(unlockDividerBookIcon);
  mask-image: v-bind(unlockDividerBookIcon);
  -webkit-mask-size: contain;
  mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
}

.divider-line {
  flex: 1;
  max-width: 120px;
  height: 1px;
  background: linear-gradient(to right, var(--unlock-divider-line-color, #D2D6DE) 0%, var(--unlock-divider-line-color, #D2D6DE) 28%, transparent 100%);
}

.divider-line-left {
  background: linear-gradient(to left, var(--unlock-divider-line-color, #D2D6DE) 0%, var(--unlock-divider-line-color, #D2D6DE) 28%, transparent 100%);
}

.divider-line-right {
  background: linear-gradient(to right, var(--unlock-divider-line-color, #D2D6DE) 0%, var(--unlock-divider-line-color, #D2D6DE) 28%, transparent 100%);
}
</style>
